import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Switch,
  TextField,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Button,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import api from "../../utils/api";

// ─── Constants ────────────────────────────────────────────────────────────────

const MODULES = [
  { id: 1, label: "Work Orders", statusEndpoint: "/statuses/workorders", color: "#2f63ad" },
  { id: 2, label: "Violations", statusEndpoint: "/statuses/violations", color: "#dc2626" },
  { id: 3, label: "Architectural", statusEndpoint: "/statuses/architectural", color: "#16a34a" },
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const paperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.98)",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "8px", backgroundColor: "#fff" },
};

// ─── StatusRow ────────────────────────────────────────────────────────────────

const StatusRow = ({ row, templates, onChange, onSave }) => {
  const isDirty = row.isDirty;
  const hasExistingTrigger = Boolean(row.trigger_id);

  return (
    <TableRow
      sx={{
        backgroundColor: isDirty ? "rgba(47,99,173,0.025)" : "transparent",
        transition: "background-color 0.15s",
        "&:last-child td": { border: 0 },
      }}
    >
      {/* Status name */}
      <TableCell sx={{ minWidth: 150 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {row.color && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: row.color,
                flexShrink: 0,
              }}
            />
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
            {row.status_name}
          </Typography>
          {isDirty && (
            <Chip
              label="unsaved"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.68rem", fontWeight: 600 }}
            />
          )}
        </Box>
      </TableCell>

      {/* Template selector */}
      <TableCell sx={{ minWidth: 220 }}>
        <TextField
          select
          size="small"
          fullWidth
          value={row.email_template_id ?? ""}
          onChange={(e) => onChange(row.status_id, "email_template_id", e.target.value || null)}
          placeholder="Use default"
          sx={fieldSx}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">
            <em style={{ color: "#9aa5b4" }}>Use module default</em>
          </MenuItem>
          {templates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name || t.subject || `Template #${t.id}`}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>

      {/* Send to member */}
      <TableCell align="center" sx={{ minWidth: 80 }}>
        <Tooltip title="Send email to property owner">
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
            <PersonIcon sx={{ fontSize: 16, color: "#6b778c" }} />
            <Switch
              size="small"
              checked={Boolean(row.send_to_member)}
              onChange={(e) => onChange(row.status_id, "send_to_member", e.target.checked)}
              color="primary"
            />
          </Box>
        </Tooltip>
      </TableCell>

      {/* Send to internal */}
      <TableCell align="center" sx={{ minWidth: 80 }}>
        <Tooltip title="Send to internal team (Notifications table)">
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
            <GroupIcon sx={{ fontSize: 16, color: "#6b778c" }} />
            <Switch
              size="small"
              checked={Boolean(row.send_to_internal)}
              onChange={(e) => onChange(row.status_id, "send_to_internal", e.target.checked)}
              color="primary"
            />
          </Box>
        </Tooltip>
      </TableCell>

      {/* Custom recipients */}
      <TableCell sx={{ minWidth: 200 }}>
        <Tooltip title="Additional comma-separated email addresses">
          <TextField
            size="small"
            placeholder="email@example.com, ..."
            value={row.custom_recipients ?? ""}
            onChange={(e) =>
              onChange(row.status_id, "custom_recipients", e.target.value)
            }
            sx={{ ...fieldSx, width: "100%" }}
          />
        </Tooltip>
      </TableCell>

      {/* Active toggle */}
      <TableCell align="center" sx={{ minWidth: 70 }}>
        <Switch
          size="small"
          checked={Boolean(row.is_active ?? true)}
          onChange={(e) => onChange(row.status_id, "is_active", e.target.checked)}
          color="success"
        />
      </TableCell>

      {/* Save */}
      <TableCell align="right" sx={{ minWidth: 60 }}>
        <Tooltip
          title={
            isDirty
              ? hasExistingTrigger
                ? "Save changes"
                : "Create trigger"
              : "No changes"
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={() => onSave(row)}
              disabled={!isDirty}
              sx={{
                color: isDirty ? (hasExistingTrigger ? "#2f63ad" : "#16a34a") : undefined,
              }}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

// ─── ModulePanel ──────────────────────────────────────────────────────────────

const ModulePanel = ({ module, templates }) => {
  const [statuses, setStatuses] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [pendingEdits, setPendingEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [bulkToggling, setBulkToggling] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const showSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });

  useEffect(() => {
    setPendingEdits({});
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, triggerRes] = await Promise.all([
        api.get(module.statusEndpoint),
        api.get("/status-email-triggers", { params: { module_id: module.id } }),
      ]);
      setStatuses(statusRes.data || []);
      setTriggers(triggerRes.data || []);
    } catch {
      showSnack("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Merge statuses + triggers into display rows
  const rows = useMemo(() => {
    return statuses.map((status) => {
      const trigger = triggers.find((t) => Number(t.status_id) === Number(status.id));
      const pending = pendingEdits[status.id] || {};
      return {
        status_id: status.id,
        status_name: status.status_name || status.statusname || status.name || status.title || `Status #${status.id}`,
        color: status.color || status.status_color || null,
        trigger_id: trigger?.id ?? null,
        // Merge trigger values with pending edits (pending wins)
        email_template_id: pending.email_template_id !== undefined
          ? pending.email_template_id
          : trigger?.email_template_id ?? null,
        send_to_member: pending.send_to_member !== undefined
          ? pending.send_to_member
          : Boolean(trigger?.send_to_member ?? true),
        send_to_internal: pending.send_to_internal !== undefined
          ? pending.send_to_internal
          : Boolean(trigger?.send_to_internal ?? true),
        custom_recipients: pending.custom_recipients !== undefined
          ? pending.custom_recipients
          : trigger?.custom_recipients ?? "",
        is_active: pending.is_active !== undefined
          ? pending.is_active
          : Boolean(trigger?.is_active ?? true),
        isDirty: Object.keys(pending).length > 0,
      };
    });
  }, [statuses, triggers, pendingEdits]);

  const handleChange = (statusId, field, value) => {
    setPendingEdits((prev) => ({
      ...prev,
      [statusId]: { ...(prev[statusId] || {}), [field]: value },
    }));
  };

  const handleSave = async (row) => {
    const payload = {
      module_id: module.id,
      status_id: row.status_id,
      email_template_id: row.email_template_id || null,
      send_to_member: Boolean(row.send_to_member),
      send_to_internal: Boolean(row.send_to_internal),
      custom_recipients: row.custom_recipients?.trim() || null,
      is_active: Boolean(row.is_active),
    };

    try {
      if (row.trigger_id) {
        await api.put(`/status-email-triggers/${row.trigger_id}`, payload);
      } else {
        await api.post("/status-email-triggers", payload);
      }
      // Clear pending for this status
      setPendingEdits((prev) => {
        const copy = { ...prev };
        delete copy[row.status_id];
        return copy;
      });
      showSnack(`Saved. ${row.status_name}`);
      // Refresh triggers to get new IDs
      const res = await api.get("/status-email-triggers", { params: { module_id: module.id } });
      setTriggers(res.data || []);
    } catch {
      showSnack(`Error saving ${row.status_name}`, "error");
    }
  };

  const handleSaveAll = async () => {
    const dirtyRows = rows.filter((r) => r.isDirty);
    if (dirtyRows.length === 0) return;
    let saved = 0;
    let failed = 0;
    for (const row of dirtyRows) {
      try {
        await handleSave(row);
        saved++;
      } catch {
        failed++;
      }
    }
    if (failed === 0) showSnack(`Saved ${saved} trigger(s)`);
    else showSnack(`${saved} saved, ${failed} failed`, "warning");
  };

  const handleBulkToggle = async (isActive) => {
    setBulkToggling(true);
    try {
      await api.put("/status-email-triggers/bulk-toggle", {
        module_id: module.id,
        is_active: isActive,
      });
      showSnack(isActive ? "All triggers enabled" : "All triggers disabled");
      fetchData();
    } catch {
      showSnack("Bulk toggle failed", "error");
    } finally {
      setBulkToggling(false);
    }
  };

  const dirtyCount = rows.filter((r) => r.isDirty).length;
  const activeCount = triggers.filter((t) => t.is_active).length;
  const totalTriggers = triggers.length;

  return (
    <Box>
      {/* Module-level control bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2.5,
          p: 2,
          borderRadius: "12px",
          backgroundColor: "rgba(47,99,173,0.04)",
          border: "1px solid rgba(47,99,173,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EmailIcon sx={{ color: module.color, fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
            {module.label} Triggers
          </Typography>
          {totalTriggers > 0 && (
            <Chip
              label={`${activeCount}/${totalTriggers} active`}
              size="small"
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.72rem",
                backgroundColor:
                  activeCount === totalTriggers
                    ? "rgba(22,163,74,0.1)"
                    : "rgba(107,119,140,0.1)",
                color: activeCount === totalTriggers ? "#16a34a" : "#6b778c",
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Enable all triggers for this module">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                bulkToggling ? (
                  <CircularProgress size={14} />
                ) : (
                  <PowerSettingsNewIcon fontSize="small" />
                )
              }
              onClick={() => handleBulkToggle(true)}
              disabled={bulkToggling}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                color: "#16a34a",
                borderColor: "rgba(22,163,74,0.4)",
                "&:hover": { borderColor: "#16a34a", backgroundColor: "rgba(22,163,74,0.06)" },
              }}
            >
              Enable All
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Disable all triggers for this module">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PowerSettingsNewIcon fontSize="small" />}
              onClick={() => handleBulkToggle(false)}
              disabled={bulkToggling}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                color: "#6b778c",
                borderColor: "rgba(107,119,140,0.3)",
              }}
            >
              Disable All
            </Button>
          </span>
        </Tooltip>

        <IconButton size="small" onClick={fetchData} disabled={loading}>
          {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>

        {dirtyCount > 0 && (
          <Chip
            icon={<SaveIcon sx={{ fontSize: "16px !important" }} />}
            label={`Save All (${dirtyCount})`}
            clickable
            onClick={handleSaveAll}
            color="primary"
            sx={{ fontWeight: 700, height: 28 }}
          />
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : statuses.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: "10px" }}>
          No statuses found for {module.label}. Make sure statuses are configured in the Statuses
          section.
        </Alert>
      ) : (
        <Paper elevation={0} sx={paperSx}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(47,99,173,0.03)" }}>
                  <TableCell sx={{ fontWeight: 700, color: "#223142" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      Email Template
                      <Tooltip title="Which email template to send. Leave blank to use the module default.">
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "rgba(47,99,173,0.5)" }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#223142" }}>
                    <Tooltip title="Send to property owner / member">
                      <span>Member</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#223142" }}>
                    <Tooltip title="Send to internal team (Notifications list)">
                      <span>Internal</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#223142" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      Extra Recipients
                      <Tooltip title="Comma-separated additional email addresses for this specific status.">
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: "rgba(47,99,173,0.5)" }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: "#223142" }}>
                    Active
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#223142" }}>
                    Save
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <StatusRow
                    key={row.status_id}
                    row={row}
                    templates={templates}
                    onChange={handleChange}
                    onSave={handleSave}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StatusEmailTriggers = () => {
  const [moduleTab, setModuleTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/email-templates")
      .then((res) => setTemplates(res.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setTemplatesLoaded(true));
  }, []);

  const currentModule = MODULES[moduleTab];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Status Email Triggers
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Configure which email is sent when a record reaches each status. Triggers are pre-seeded
          from your existing statuses. adjust templates and recipients as needed.
        </Typography>
      </Box>

      {/* Module tabs */}
      <Tabs
        value={moduleTab}
        onChange={(_, v) => setModuleTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            minHeight: 38,
            color: "#5f6b7a",
            mr: 1,
            px: 2,
          },
          "& .Mui-selected": {
            color: "#2f63ad !important",
            backgroundColor: "rgba(47,99,173,0.08)",
          },
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        {MODULES.map((m, i) => (
          <Tab key={i} label={m.label} />
        ))}
      </Tabs>

      <Divider sx={{ mb: 3 }} />

      {templatesLoaded ? (
        <ModulePanel
          key={currentModule.id}
          module={currentModule}
          templates={templates}
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default StatusEmailTriggers;
